import moment from 'moment-timezone';

/**
 * The agreed display states for a user's shared location, in one place so the
 * map pins and the roster panel can never disagree about what someone's dot
 * means.
 *
 * Staleness is NOT recomputed here — the server sends `is_stale`, so the
 * window it is measured against lives in exactly one place.
 */

export const TONE = {
  LIVE: 'live',
  INACTIVE: 'inactive',
  DISABLED: 'disabled',
};

export const TONE_COLOR = {
  [TONE.LIVE]: '#2f9e44',
  [TONE.INACTIVE]: '#9ca3af',
  [TONE.DISABLED]: '#c0392b',
};

/**
 * Compact relative time: `Now`, `3m ago`, `2h ago`, `4d ago`.
 *
 * Counted from the timestamp of the last data point, never from when the list
 * request was answered.
 */
export const relativeTime = (lastUpdatedAt) => {
  if (!lastUpdatedAt) {
    return null;
  }

  const minutes = moment.utc().diff(moment.utc(lastUpdatedAt), 'minutes');
  if (minutes < 1) {
    return 'Now';
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  return `${Math.floor(hours / 24)}d ago`;
};

/**
 * Resolve one person's row into { label, tone, plottable }.
 *
 * `plottable` drives whether a pin is drawn at all. A disabled user has no
 * coordinates to draw, and someone who has never reported has none either.
 *
 * Off-property and stale positions ARE still drawn, greyed: the contract
 * leaves that choice open ("hide or mark as inactive"), and a greyed pin
 * labelled `47m ago` states what is known without implying the person is
 * standing there now, whereas hiding it throws away the last thing we knew.
 */
export const resolveState = (person) => {
  const hasPosition =
    person.latitude !== null && person.latitude !== undefined &&
    person.longitude !== null && person.longitude !== undefined;

  // A POSITION WINS over the sharing flag, and the order matters. The flag is
  // the web user's own browser switch; it does not govern what a mobile app
  // reports. Someone whose phone is reporting while their browser toggle is
  // off still has a real position, and testing the flag first would hide them.
  // The server has already withheld anything that must not be shown.
  if (!hasPosition) {
    if (!person.sharing_enabled) {
      return { labelKey: 'propertyMapPage.userLocations.userDisabled', tone: TONE.DISABLED, plottable: false };
    }
    return { labelKey: 'propertyMapPage.userLocations.unavailable', tone: TONE.INACTIVE, plottable: false };
  }

  if (person.is_stale) {
    return { labelKey: 'propertyMapPage.userLocations.unavailable', tone: TONE.INACTIVE, plottable: true };
  }

  if (person.is_on_property === false) {
    return { labelKey: 'propertyMapPage.userLocations.offProperty', tone: TONE.INACTIVE, plottable: true };
  }

  // is_on_property true, or null because the property has no mapped areas to
  // test against. Either way the fix is fresh, so it reads as live; the area
  // name is shown when the server could resolve one.
  return {
    // An area name is real data, not a translatable string; fall back to a key
    // when the server could not resolve one.
    label: person.area_name || null,
    labelKey: person.area_name ? null : 'propertyMapPage.userLocations.onProperty',
    tone: TONE.LIVE,
    plottable: true,
  };
};

/**
 * Roster ordering: the people with something to say, first.
 *
 * The panel lists the property's whole membership, which on a real property
 * means dozens of "Location Unavailable" rows for people who have never
 * reported. Left in table order they bury the handful of people actually on
 * site, which is the one thing the panel exists to show.
 *
 *   0  live, on property        — the answer to "who is here now"
 *   1  off property or stale    — reported at some point, position is known
 *   2  sharing disabled         — an explicit choice, worth seeing
 *   3  never reported           — carries no information at all
 *
 * Within a rank, most recently heard from first, then by name so the order is
 * stable between polls.
 */
const rosterRank = (person, state) => {
  if (state.tone === TONE.LIVE) {
    return 0;
  }
  if (state.tone === TONE.DISABLED) {
    return 2;
  }
  return person.last_updated_at ? 1 : 3;
};

export const compareForRoster = (a, b) => {
  const rankDiff = rosterRank(a, resolveState(a)) - rosterRank(b, resolveState(b));
  if (rankDiff !== 0) {
    return rankDiff;
  }

  if (a.last_updated_at !== b.last_updated_at) {
    // Newest first; a missing timestamp sorts last within the rank.
    if (!a.last_updated_at) return 1;
    if (!b.last_updated_at) return -1;
    return a.last_updated_at < b.last_updated_at ? 1 : -1;
  }

  return (a.name || '').localeCompare(b.name || '');
};
